import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/hooks/use-session";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: AdminHome,
});

type Pending = { id: string; email: string; full_name: string | null; created_at: string };
type UserRow = { id: string; email: string; full_name: string | null; status: string; role: string | null };
type Role = "superadmin" | "editor" | "viewer";
const ROLE_LABEL: Record<Role, string> = { superadmin: "Superadmin", editor: "Escritor", viewer: "Visitante" };

function AdminHome() {
  const { isSuperadmin } = useSession();
  const [pending, setPending] = useState<Pending[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [leadCount, setLeadCount] = useState(0);
  const [formCount, setFormCount] = useState(0);
  const [meetingCount, setMeetingCount] = useState(0);

  async function load() {
    const [p, allProfiles, allRoles, cAll, cForm, cMeeting] = await Promise.all([
      supabase.from("profiles").select("id,email,full_name,created_at").eq("status", "pending_approval").order("created_at", { ascending: false }),
      supabase.from("profiles").select("id,email,full_name,status").order("email"),
      supabase.from("user_roles").select("user_id,role"),
      supabase.from("leads").select("*", { count: "exact", head: true }),
      supabase.from("leads").select("*", { count: "exact", head: true }).eq("source", "form"),
      supabase.from("leads").select("*", { count: "exact", head: true }).eq("source", "meeting"),
    ]);
    setPending((p.data ?? []) as Pending[]);
    setLeadCount(cAll.count ?? 0);
    setFormCount(cForm.count ?? 0);
    setMeetingCount(cMeeting.count ?? 0);

    const roleByUser: Record<string, string> = {};
    (allRoles.data ?? []).forEach((r: { user_id: string; role: string }) => { roleByUser[r.user_id] = r.role; });
    setUsers(((allProfiles.data ?? []) as Array<{ id: string; email: string; full_name: string | null; status: string }>).map((u) => ({
      ...u, role: roleByUser[u.id] ?? null,
    })));
  }

  useEffect(() => { load(); }, []);

  async function approve(id: string, approved: boolean) {
    await supabase.from("profiles").update({
      status: approved ? "approved" : "rejected",
      approved_at: approved ? new Date().toISOString() : null,
    }).eq("id", id);
    await supabase.from("admin_audit_log").insert({
      actor_id: (await supabase.auth.getUser()).data.user?.id,
      action: approved ? "approve_user" : "reject_user",
      target_type: "profile",
      target_id: id,
    });
    load();
  }

  async function setRole(userId: string, role: Role) {
    await supabase.from("user_roles").delete().eq("user_id", userId);
    await supabase.from("user_roles").insert({ user_id: userId, role });
    load();
  }

  async function setStatus(userId: string, status: "approved" | "rejected" | "pending_approval") {
    await supabase.from("profiles").update({
      status,
      approved_at: status === "approved" ? new Date().toISOString() : null,
    }).eq("id", userId);
    load();
  }

  return (
    <div className="space-y-8">
      <section className="grid md:grid-cols-4 gap-4">
        <Kpi label="Leads totales" value={leadCount} to="/admin/leads" />
        <Kpi label="Formulario" value={formCount} to="/admin/leads" />
        <Kpi label="Solicitudes de reunión" value={meetingCount} to="/admin/leads" />
        <Kpi label="Pendientes de aprobación" value={pending.length} />
      </section>

      <div className="bg-white border border-[var(--color-outline-variant)] p-6">
        <h2 className="font-bold text-lg mb-2">Gestión de leads</h2>
        <p className="text-sm text-[var(--color-on-surface-variant)] mb-4">
          Todos los leads, atribución UTM, filtros y export CSV están en el panel dedicado.
        </p>
        <Link to="/admin/leads" className="inline-block px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">
          Abrir panel de leads →
        </Link>
      </div>

      {isSuperadmin && (
        <Section title={`Usuarios pendientes (${pending.length})`}>
          <ul className="divide-y divide-[var(--color-outline-variant)]">
            {pending.map((u) => (
              <li key={u.id} className="px-6 py-4 flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold">{u.full_name ?? u.email}</div>
                  <div className="text-xs text-[var(--color-on-surface-variant)]">
                    {u.email} · solicitado {new Date(u.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approve(u.id, false)} className="px-3 py-1.5 border border-[var(--color-outline-variant)] text-sm">
                    Rechazar
                  </button>
                  <button onClick={() => approve(u.id, true)} className="px-3 py-1.5 bg-[var(--color-brand-coral)] text-white text-sm font-bold">
                    Aprobar
                  </button>
                </div>
              </li>
            ))}
            {pending.length === 0 && (
              <li className="px-6 py-8 text-center text-[var(--color-on-surface-variant)]">Sin solicitudes pendientes.</li>
            )}
          </ul>
        </Section>
      )}

      {isSuperadmin && (
        <Section title={`Gestión de usuarios (${users.length})`}>
          <div className="px-6 py-3 text-xs text-[var(--color-on-surface-variant)] bg-[var(--color-surface-container-low)] border-b border-[var(--color-outline-variant)]">
            Solo cuentas <strong>@mine-class.cl</strong> pueden ingresar (iniciando sesión con Google).
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface-container-low)] text-left">
                <tr>
                  <th className="px-4 py-3">Correo</th>
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Rol</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-[var(--color-outline-variant)]">
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3">{u.full_name ?? "—"}</td>
                    <td className="px-4 py-3">
                      <select value={u.status} onChange={(e) => setStatus(u.id, e.target.value as "approved" | "rejected" | "pending_approval")}
                        className="border border-[var(--color-outline-variant)] px-2 py-1 text-xs bg-white">
                        <option value="approved">Aprobado</option>
                        <option value="pending_approval">Pendiente</option>
                        <option value="rejected">Rechazado</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select value={(u.role as Role) ?? "viewer"} onChange={(e) => setRole(u.id, e.target.value as Role)}
                        className="border border-[var(--color-outline-variant)] px-2 py-1 text-xs bg-white">
                        <option value="superadmin">{ROLE_LABEL.superadmin}</option>
                        <option value="editor">{ROLE_LABEL.editor}</option>
                        <option value="viewer">{ROLE_LABEL.viewer}</option>
                      </select>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-[var(--color-on-surface-variant)]">Aún no hay usuarios.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}

function Kpi({ label, value, to }: { label: string; value: number; to?: string }) {
  const body = (
    <div className="bg-white border border-[var(--color-outline-variant)] p-6 hover:border-[var(--color-brand-coral)] transition-colors">
      <div className="text-xs uppercase tracking-widest text-[var(--color-on-surface-variant)]">{label}</div>
      <div className="text-3xl font-bold mt-2 text-[var(--color-brand-purple)]">{value}</div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white border border-[var(--color-outline-variant)]">
      <div className="px-6 py-4 border-b border-[var(--color-outline-variant)]">
        <h2 className="font-bold">{title}</h2>
      </div>
      {children}
    </section>
  );
}
